import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell } from "recharts";
import {
  TrendingUp, Wallet, ArrowDownRight, ArrowUpRight, PlusCircle, Receipt as ReceiptIcon,
  Users, Scale, Loader2, ArrowRight, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { money, dateFmt, categoryBadge, monthFmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { AiAssistant } from "../components/AiAssistant";
import { FixedBillsCard } from "../components/FixedBills";

const CHART_COLORS = ["#4F46E5", "#059669", "#D97706", "#06B6D4", "#E11D48", "#8B5CF6", "#0EA5E9", "#F59E0B"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const s = data.summary;
  const me = data.me;
  const bal = me?.balance || 0;

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{monthFmt(s.month)}</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Hi {user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <Button onClick={() => navigate("/add")} data-testid="dashboard-add-btn" className="rounded-full font-semibold shadow-md shadow-primary/20 hidden sm:flex">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add expense
        </Button>
      </div>

      {/* Balance hero */}
      <div className="rounded-3xl border border-border bg-card p-6 relative overflow-hidden animate-rise" data-testid="personal-dashboard-summary">
        <div className={`absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl ${bal >= 0 ? "bg-emerald-400/20" : "bg-rose-400/20"}`} />
        <div className="relative">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Wallet className="h-4 w-4" /> Your current balance
          </div>
          <div className={`font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-1 ${bal >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="my-balance">
            {bal >= 0 ? "+" : "−"}{money(Math.abs(bal))}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {bal >= 0.01 ? "You're owed money this month" : bal <= -0.01 ? "You owe the household" : "You're all settled up"}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <MiniStat icon={ArrowUpRight} label="You paid" value={money(me?.paid)} testid="my-paid" />
            <MiniStat icon={Scale} label="Your share" value={money(me?.share)} testid="my-share" />
            <MiniStat icon={bal >= 0 ? ArrowDownRight : ArrowUpRight} label={bal >= 0 ? "Owed to you" : "You owe"} value={money(Math.abs(bal))} testid="my-owe" tone={bal >= 0 ? "pos" : "neg"} />
          </div>
        </div>
      </div>

      {/* Household overview */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-3xl border border-border bg-card p-5" data-testid="household-total-card">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="h-4 w-4" /> Household total</div>
          <div className="font-heading text-3xl font-bold mt-1">{money(s.total)}</div>
          <div className="text-xs text-muted-foreground mt-1">{s.expense_count} expenses · {s.member_count} roommates · {money(s.share)} each</div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5" data-testid="household-ledger-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><PieChartIcon /> By category</div>
          </div>
          {s.by_category.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No expenses yet</p>
          ) : (
            <div className="flex items-center gap-3">
              <div className="h-24 w-24 shrink-0">
                <PieChart width={96} height={96}>
                  <Pie data={s.by_category} dataKey="amount" nameKey="category" cx={44} cy={44} innerRadius={26} outerRadius={46} paddingAngle={2}>
                    {s.by_category.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </div>
              <div className="flex-1 space-y-1 min-w-0">
                {s.by_category.slice(0, 4).map((c, i) => (
                  <div key={c.category} className="flex items-center gap-2 text-xs">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="truncate flex-1">{c.category}</span>
                    <span className="font-semibold tabular-nums">{money(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <FixedBillsCard isAdmin={role === "admin"} memberCount={s.member_count} />

      {/* AI assistant */}
      <AiAssistant />

      {/* Roommate breakdown + recent activity: side-by-side on large screens */}
      <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 items-start">
      {/* Roommate breakdown */}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Roommate contributions</h2></div>
        <div className="space-y-3">
          {s.members.map((m) => {
            const pct = s.total > 0 ? Math.min(100, (m.paid / s.total) * 100) : 0;
            return (
              <div key={m.member_id} data-testid="roommate-row">
                <div className="flex items-center justify-between text-sm mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-7 w-7"><AvatarFallback className="text-[11px] bg-primary/10 text-primary font-semibold">{m.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                    <span className="font-medium truncate">{m.name}{m.user_id === user?.user_id ? " (you)" : ""}</span>
                    {m.role === "admin" && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">admin</Badge>}
                  </div>
                  <span className={`font-semibold tabular-nums text-xs ${m.balance >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {m.balance >= 0 ? "+" : "−"}{money(Math.abs(m.balance))}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Paid {money(m.paid)} of {money(m.share)} share</div>
              </div>
            );
          })}
        </div>
        <Button variant="ghost" onClick={() => navigate("/balance")} className="w-full mt-3 text-primary" data-testid="view-settlement-btn">
          View who owes who <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Recent activity */}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Recent activity</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/receipts")} className="text-xs text-muted-foreground">All receipts</Button>
        </div>
        {data.recent.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ReceiptIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No expenses yet.</p>
            <Button onClick={() => navigate("/add")} size="sm" className="mt-3 rounded-full">Add your first expense</Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {data.recent.map((e) => (
              <div key={e.id} className="flex items-center gap-3" data-testid="recent-expense-item">
                <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                  {e.receipt_file_id ? <ReceiptIcon className="h-4 w-4 text-primary" /> : <Wallet className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{e.name}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${categoryBadge(e.category)}`}>{e.category}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{e.submitter_name} · {dateFmt(e.date)}</div>
                </div>
                <div className="font-heading font-bold tabular-nums text-sm">{money(e.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, testid, tone }) {
  const color = tone === "pos" ? "text-emerald-600 dark:text-emerald-400" : tone === "neg" ? "text-rose-600 dark:text-rose-400" : "";
  return (
    <div className="rounded-2xl bg-secondary/60 p-3" data-testid={testid}>
      <Icon className={`h-4 w-4 mb-1 ${color || "text-muted-foreground"}`} />
      <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
      <div className={`font-heading font-bold text-base mt-0.5 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function PieChartIcon() {
  return <Scale className="h-4 w-4" />;
}
